import { Component, EventEmitter, HostListener, Input, Output, SimpleChanges } from '@angular/core';
import { TrunkBaseConfigurationService } from '@core/services/trunk-base-configuration/trunk-base-configuration.service';
import { SortingHelperService } from '@shared/helpers/utils/sorting-helper.service';
import { LeftButtonData, TableButton, TableHeader, TableHighlightedText } from '@shared/interfaces/table-headers.interface';
@Component({
  selector: 'app-so-table',
  templateUrl: './so-table.component.html',
  styleUrl: './so-table.component.scss',
})
export class SoTableComponent {
  @Input({ required: true }) set tableData(value: Record<string, any>[]) {
    if (value) {
      this._tableData = this.cloneData(value);
      this.markForCheck();
    }
  }
  @Input({required:true}) tableHeaders: TableHeader[] = [];
  @Input({required:true}) isActionsEnabled: boolean = false;
  @Input({required:true}) enableRowSelection:boolean = false;
  @Input({required:true}) showSwitches: boolean = false;
  @Input({required:true}) enablePagination: boolean = false;
  @Input() lang: string = '';
  @Input() tableFeet: Record<string, any> | null = null;
  @Input() deleteAction: boolean = true;
  @Input() editAction: boolean = true;
  @Input() itemsPerPage: number = 15;
  @Input() currentPage: number = 1;
  @Input() loadingItems: boolean = false;
  // se requiere cuando el showSwitches es true
  @Input() switchHeader:string = ''
  // se requiere cuando el showSwitches es true
  @Input() switchIdentifier: string = '';
  @Input() switchLabelLeft:string = 'No';
  @Input() switchLabelRight:string = 'Si';
  @Input() enableSaveButton:boolean = false;
  @Input() enableLeftButton:boolean = false;
  @Input() enableAllSwitches:boolean = true;
  @Input() headerStyle: 'classic' | 'new' = 'new';
  @Input() leftButtonData: LeftButtonData = {text: '', icon: '', disabled: true, tooltip: ''};
  @Input() searchText: string = '';
  @Input() buttonsContainerWidth: string = 'auto';
  @Input() buttonsContainerPaddingLeft: string | null = null;
  @Input() paddingBottomClass: 0 | 1 | 2 | 3 | 4 | 5 = 5;
  @Input() customButtons: TableButton[] = [];
  @Input() disabledSwitchTooltip: string = 'El distribuidor debe estar activo';
  @Input() rowColorFunction: ((item: any) => string) | undefined;
  @Input() rowSwitchDisabledFunction: ((item: any) => boolean) | undefined;
  @Input() textWrap: boolean = true;
  @Input() checkTextTruncated: boolean = true;
  @Input() firstColumnStickyLeft: boolean = false;
  @Input() containerCustomStyle: string = '';
  @Input() fixHeadCustomStyle: string = '';
  @Input() headersHeight: string = '';
  @Input() rowsHeight: string = '45px';
  @Input() headersColorIsPrimary: boolean = true;
  @Input() headersCaseIsUpper: boolean = true;
  @Input() firstColumnPaddingLeft: string = '5%';
  @Input() lastColumnPaddingRight: string = '1rem';
  @Input() footerHighlight: boolean = true;
  @Input() rowSwitchDisabledTooltipFunction: ((item: any) => string) | undefined;
  // Nueva columna opcional de iconos (al estilo de showSwitches)
  @Input() showIcons: boolean = false;
  @Input() iconsHeader: string = '';
  // Nombre del campo dentro de cada fila que contiene el arreglo de iconos
  // Cada elemento del arreglo debe tener: { icon: string, iconColor: string, iconSize: number, tooltipText?: string }
  @Input() iconsIdentifier: string = '';
  @Output() switchEvent: EventEmitter<{data:Record<string, any>, status:boolean}> = new EventEmitter();
  @Output() rowClickedEvent: EventEmitter<Record<string, any>> = new EventEmitter();
  @Output() saveEvent: EventEmitter<Record<string, any>[]> = new EventEmitter();
  @Output() editEvent: EventEmitter<Record<string, any>> = new EventEmitter();
  @Output() deleteEvent: EventEmitter<Record<string, any>> = new EventEmitter();
  @Output() leftButtonEvent: EventEmitter<void> = new EventEmitter();

  protected _tableData: Record<string, any>[] = [];
  protected originalSet: Record<string, any>[] = [];
  protected sortDirection: 'asc' | 'desc' = 'asc';
  protected truncateCache = new WeakMap<HTMLElement, boolean>();
  public firstColumnIndex: number = 0;
  public lastColumnIndex: number = 0;
  public filteredData: Record<string, any>[] = [];
  public selectedRow: Record<string, any> | null = null;

  public tableFeetHighlights: TableHighlightedText[][] = [];
  public rowsHighlights: TableHighlightedText[][][] = [];

  public filterBy: string = '';

  constructor(
    private sortingHelperService: SortingHelperService,
    private trunkBaseConfigurationService: TrunkBaseConfigurationService,
  ){
  }
  ngOnInit(): void {
    this.filterBy = '';
    this.originalSet = JSON.parse(JSON.stringify(this.tableData));
    this.tableData =  this.tableData.map((element) => {
      if(this.showSwitches) element = {...element, disabledSwitch: !this.enableAllSwitches}
      return element;
    });
    this.filteredData = this.tableData;
    this.updateDisplayedData();
    this.filteredData =  this.filteredData.map((element) => {
      if(this.showSwitches) element = {...element, disabledSwitch: !this.enableAllSwitches}
      return element;
    });
    this.markForCheck();
  }
  ngOnChanges(changes: SimpleChanges): void {
    // Cambios en TableHeaders
    if (changes['tableHeaders']) {
      // Buscar índices del primer y el último header visibles
      this.firstColumnIndex = Math.max(0, this.tableHeaders.findIndex(header => this.shouldShowColumn(header)));
      for (let i = this.tableHeaders.length - 1; i >= 0; i--)
        if (this.shouldShowColumn(this.tableHeaders[i])) {
          this.lastColumnIndex = i;
          break;
        }
    }
    //Cambios en TableData
    if (changes['tableData'] && !changes['tableData'].isFirstChange()) {
      this.handleTableDataChange(changes['tableData'].previousValue.length);
    }
    //Deshabilitar todos los switches
    if(changes['enableAllSwitches']){
      if(this.enablePagination){
        this.filteredData = this.filteredData.map((data) => {
          data['disabledSwitch'] = !changes['enableAllSwitches'].currentValue;
          return data
        })
      }else{
        this.tableData = this.tableData.map((data) => {
          data.disabledSwitch = !changes['enableAllSwitches'].currentValue;
          return data
        })
      }
    }
    if(changes['searchText'] && !changes['searchText'].isFirstChange()){
      this.handleSearch(changes['searchText'].currentValue);
    }
    if(changes['leftButtonData'] && !changes['leftButtonData'].isFirstChange()){
      this.leftButtonData = changes['leftButtonData'].currentValue;
    }
    if(changes['tableFeet'] && this.tableFeet !== null && this.footerHighlight)
      this.setHighlights(this.tableFeet, this.tableFeetHighlights);
    this.resetTruncateCache();
    this.markForCheck();
  }
  handleTableDataChange( previousTableDataLenght: number): void {
    this.filterBy = '';
    this.originalSet = JSON.parse(JSON.stringify(this.tableData)); // Copia profunda
    if(previousTableDataLenght != this.originalSet.length) this.currentPage = 1;
    this.tableData = this.originalSet.map((element) => {
      if(this.showSwitches){
        element = {...element, disabledSwitch: !this.enableAllSwitches};
      }
      return element;
    })
    if(this.tableData.length != this.originalSet.length) this.currentPage = 1;
    if(this.enablePagination){
      this.filteredData = this.tableData.map((element) => {
        if(this.showSwitches) element = {...element, disabledSwitch: !this.enableAllSwitches};
        return element;
      });
    }
    this.updateDisplayedData();
    if(this.searchText){
      this.handleSearch(this.searchText);
    }
    if (this.enableRowSelection && this.selectedRow) {
      const selectedRowJSONValue = JSON.stringify(this.selectedRow);
      this.selectedRow = this.tableData.find(item => JSON.stringify(item) === selectedRowJSONValue) ?? null;
    }
    this.markForCheck();
  }
  handleSearch(value: string): void {
    if (value) {
      if(this.enablePagination){
        this.filteredData = this._tableData.filter((item) =>
          Object.keys(item).some(key =>
            item[key] && item[key].toString().toLowerCase().includes(value.toLowerCase())
          )
        );
        const startIndex = (this.currentPage - 1) * this.itemsPerPage;
        const endIndex = startIndex + this.itemsPerPage;
        this.filteredData = this.filteredData.slice(startIndex, endIndex);
      }else{
        this.tableData = this.tableData.filter((item) =>
          Object.keys(item).some(key =>
            item[key] && item[key].toString().toLowerCase().includes(value.toLowerCase())
          )
        );
      }
    } else {
      if(this.enablePagination){
        this.filteredData = [...this.originalSet];
        this.currentPage = 1;
        this.updateDisplayedData();
      }else{
        this.currentPage = 1;
        this.tableData = [...this.originalSet]
      }
    }
    this.markForCheck();
  }
  save(){
    this.saveEvent.emit();
  }
  getPropertyValue(item: any, path: string): any {
    return path.split('.').reduce((o, p) => o?.[p], item);
  }
  editRow(data:Record<string, any>){
    this.editEvent.emit(data);
  }
  deleteRow(data:Record<string, any>){
    this.deleteEvent.emit(data);
  }
  isButtonDisabled(button: TableButton, item: any): boolean {
    if (typeof button.disabled === 'function') {
      return button.disabled(item);
    }
    return !!button.disabled;
  }
  isTextTruncated(element: HTMLElement): boolean {
    if (!element || !this.checkTextTruncated) {
      return false;
    }
    const cached = this.truncateCache.get(element);
    if (cached !== undefined) {
      return cached;
    }
    const isTruncated =
      element.clientWidth < element.scrollWidth ||
      element.clientHeight < element.scrollHeight;
    this.truncateCache.set(element, isTruncated);
    setTimeout(() => {
      this.truncateCache.set(
        element,
        element.clientWidth < element.scrollWidth || element.clientHeight < element.scrollHeight
      );
    });
    return isTruncated;
  }
  getColspan(): number {
    let count = this.tableHeaders.length;
    if (this.isActionsEnabled) count += 1;
    if (this.showSwitches) count += 1;
    if (this.showIcons) count += 1;
    return count;
  }
  protected  getSortGetter(field: string): (row: any) => any {
    const header = this.tableHeaders.find(h => h.dataName === field);
    if (header?.convertFunction) {
      return (row: any) => header.convertFunction!(this.getPropertyValue(row, field), row);
    }
    return (row: any) => this.getPropertyValue(row, field);
  }
  onClickShortByData(field: string){
    this.filterBy = field;
    const getter = this.getSortGetter(field);
    if(this.enablePagination){
      this.filteredData = this.sortingHelperService.sortArrayByGetter(this.tableData, getter, this.sortDirection);
    }else{
      this.tableData = this.sortingHelperService.sortArrayByGetter(this.tableData, getter, this.sortDirection);
    }
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.currentPage = 1;
    if(this.enablePagination) this.updateDisplayedData();
    this.setRowsHighlights();
    this.markForCheck();
  }
  onPageChanged(newPage: number): void {
    this.currentPage = newPage;
    this.updateDisplayedData();
  }
  updateDisplayedData(): void {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    if(this.filterBy != ''){
      const getter = this.getSortGetter(this.filterBy);
      const filteredTableData = this.sortingHelperService.sortArrayByGetter(this.tableData, getter, this.sortDirection);
      this.filteredData = filteredTableData.slice(startIndex, endIndex);
    }else{
      this.filteredData = this.tableData.slice(startIndex, endIndex);
    }
    this.setRowsHighlights();
  }
  protected  setRowsHighlights(){
    if (!this.tableHeaders.some(header => header.hightlightText !== undefined))
      return;

    this.rowsHighlights = [];
    (this.enablePagination ? this.filteredData : this.tableData).forEach(rowDataArray => {
      const rowHighlights: TableHighlightedText[][] = [];
      this.rowsHighlights.push(rowHighlights);
      this.setHighlights(rowDataArray, rowHighlights);
    });
  }
  onSwitchActivated(data:Record<string, any>, status: boolean){
    if(data != this.selectedRow) this.rowClickedEvent.emit(data);
    data[this.switchIdentifier] = status
    this.selectedRow = data;
    this.switchEvent.emit({ data, status });
    this.markForCheck();
  }
  onRowClic(data:Record<string, any>){
    this.selectedRow = data;
    this.rowClickedEvent.emit(data);
    this.markForCheck();
  }
  @HostListener('window:resize')
  onWindowResize(): void {
    this.resetTruncateCache();
  }
  private resetTruncateCache(): void {
    this.truncateCache = new WeakMap();
  }
  get tableData(): any[] {
    return this._tableData;
  }
  leftButtonAction(){
    this.leftButtonEvent.emit();
  }
  shouldShowColumn(header: TableHeader): boolean {
    if (header.showColumn === undefined) return true;
    if (typeof header.showColumn === 'function') return header.showColumn();
    return header.showColumn;
  }
  toString(value: any): string {
    return String(value);
  }
  setHighlights(rowDataArray: Record<string, any>, hightlightsArray: TableHighlightedText[][]) { // Establece los textos resaltados de la fila indicada
    hightlightsArray.length = 0;

    this.tableHeaders.forEach(tableHeader => {
      const tableCellText = this.toString(rowDataArray[tableHeader.dataName]);

      if (tableHeader.hightlightText?.isHighlightableFn)
        if (!tableHeader.hightlightText.isHighlightableFn(rowDataArray)) {
          hightlightsArray.push([{ text: tableCellText, highlighted: false }]);
          return;
        }

      if (!tableHeader.hightlightText) {
        hightlightsArray.push([{ text: tableCellText, highlighted: false }]);
        return;
      }
      const match = tableCellText.match(tableHeader.hightlightText.regex);
      if (!match) {
        hightlightsArray.push([{ text: tableCellText, highlighted: false }]);
        return;
      }

      if (match.length === 1)
        hightlightsArray.push([{ text: tableCellText, highlighted: true }]);
      else
        hightlightsArray.push(match.reduce<TableHighlightedText[]>((highlights, currentMatch, i) =>
          i === 0 || !currentMatch || tableHeader.hightlightText === undefined ? highlights
          : [ ...highlights, { text: currentMatch, highlighted: i === tableHeader.hightlightText.regexGroupIndex } ], []));
    });
  }
  getTooltipContent(button: any, item: any): string {
    if (typeof button.tooltip === 'function') {
      return button.tooltip(item) ?? '';
    }
    return button.tooltip ?? '';
  }
  protected cloneData(value: Record<string, any>[]): Record<string, any>[] {
    return JSON.parse(JSON.stringify(value ?? []));
  }
  protected markForCheck(): void {}
}
